import {
  Box,
  Tabs,
  Tab,
  Button,
  Dialog,
  TextField,
  Typography,
  IconButton,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import Table from "../../components/Table";
import { toast } from "react-toastify";
import {
  Pending,
  RequestQuote,
  CheckCircle,
  Cancel,
  Edit,
  Visibility,
  Close,
  Save,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { EditLeadDialog } from "../../components/leads/EditLeadDialog";
import theme from "../../theme/theme";
import {
  fetchLeads as fetchLeadsApi,
  fetchLeadById,
  updateLead as updateLeadApi,
  rejectLead as rejectLeadApi,
  exportLeads,
  fetchLeadCounts,
} from "../../api/LeadService";
import { fetchEmployeesByDesignation } from "../../api/EmployeeService";
import { useNavigate } from "react-router-dom";

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const LeadManagementPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [fetchingLead, setFetchingLead] = useState(false);
  const [updatingLead, setUpdatingLead] = useState(false);
  const [selectedLead, setSelectedLead] = useState({});
  const [errors, setErrors] = useState({});

  // Status tab
  const [activeTab, setActiveTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  // Search and Export
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [exporting, setExporting] = useState(false);

  // Sales person filter
  const [salesPeople, setSalesPeople] = useState([]);
  const [salesPersonFilter, setSalesPersonFilter] = useState("");
  const [counts, setCounts] = useState({
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  // Reject dialog
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectingLead, setRejectingLead] = useState(false);
  const [rejectLeadId, setRejectLeadId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectErrors, setRejectErrors] = useState({});

  const editLead = (id) => {
    setOpenEditDialog(true);
    fetchLeadForEdit(id);
  };

  const fetchLeadForEdit = async (id) => {
    setFetchingLead(true);
    try {
      const response = await fetchLeadById(id);
      setSelectedLead(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch lead details for edit");
    } finally {
      setFetchingLead(false);
    }
  };

  const updateLead = async () => {
    setUpdatingLead(true);
    setErrors({});
    try {
      await updateLeadApi(selectedLead.id, selectedLead);
      toast.success("Lead updated successfully");
      setOpenEditDialog(false);
      fetchLeads();
    } catch (error) {
      if (error && error.data && error.data.errors) {
        setErrors(error.data.errors);
      } else {
        toast.error(error.message || "Failed to update lead");
      }
    } finally {
      setUpdatingLead(false);
    }
  };

  const handleOpenReject = (leadId) => {
    setRejectLeadId(leadId);
    setRejectionReason("");
    setRejectErrors({});
    setOpenRejectDialog(true);
  };

  const handleRejectLead = async () => {
    setRejectingLead(true);
    setRejectErrors({});
    try {
      await rejectLeadApi(rejectLeadId, rejectionReason);
      toast.success("Lead rejected successfully");
      setOpenRejectDialog(false);
      fetchLeads();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setRejectErrors(error.response.data.errors);
      } else {
        toast.error(error?.response?.data?.message || "Failed to reject lead");
      }
    } finally {
      setRejectingLead(false);
    }
  };

  const actions = [
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => {
        editLead(row.id);
      },
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Visibility />,
      label: "View",
      onClick: (row) => {
        navigate(`/leads/${row.id}`);
      },
      backgroundColor: theme.button.viewBackgroundColor,
    },
    {
      icon: <Cancel />,
      label: "Reject",
      onClick: (row) => {
        handleOpenReject(row.id);
      },
      backgroundColor: "#ef5350",
      show: (row) => row.status === "pending",
    },
  ];

  const statusMap = (lead) => {
    if (lead.status === "pending") {
      if (lead.latest_quotation_request) {
        if (lead.latest_quotation_request.status === "requested") {
          return {
            label: "Quotation Requested",
            color: "warning",
            icon: <RequestQuote />,
          };
        } else if (lead.latest_quotation_request.status === "created") {
          return { label: "Quotation Created", color: "success" };
        }
      }
      return { label: "Pending", color: "warning", icon: <Pending /> };
    } else if (lead.status === "approved") {
      return { label: "Approved", color: "success", icon: <CheckCircle /> };
    } else if (lead.status === "rejected") {
      return { label: "Rejected", color: "error", icon: <Cancel /> };
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await fetchLeadsApi(
        currentPage,
        null,
        salesPersonFilter || null,
        perPage,
        statusFilter || null,
        searchQuery || null,
      );
      const leads = response.data.data.map((lead) => {
        return {
          ...lead,
          statusCustom: statusMap(lead),
        };
      });
      setLeads(leads);
      setLinks(response.data.links);
      setMeta({ from: response.data.from, to: response.data.to, total: response.data.total });
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const getCounts = async () => {
    try {
      const res = await fetchLeadCounts();
      setCounts(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportLeads(
        null,
        salesPersonFilter || null,
        statusFilter || null,
        searchQuery || null,
      );
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "leads.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Failed to export leads");
    } finally {
      setExporting(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setStatusFilter(STATUS_TABS[newValue].value);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    fetchLeads();
    getCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, currentPage, searchQuery, salesPersonFilter, perPage]);

  useEffect(() => {
    const loadSalesPeople = async () => {
      try {
        const res = await fetchEmployeesByDesignation("sales");
        setSalesPeople(res.data || []);
      } catch (e) {
        // non-critical
      }
    };
    loadSalesPeople();
  }, []);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Leads Management
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            value={salesPersonFilter}
            onChange={(e) => {
              setSalesPersonFilter(e.target.value);
              setCurrentPage(1);
            }}
            sx={{ minWidth: 180, backgroundColor: "#fff", borderRadius: 1 }}
            label="Sales Person"
          >
            <MenuItem value="">All Sales People</MenuItem>
            {salesPeople.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.name}
              </MenuItem>
            ))}
          </TextField>
          <form
            onSubmit={handleSearchSubmit}
            style={{ display: "flex", gap: "8px" }}
          >
            <TextField
              size="small"
              placeholder="Search leads..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: 1 }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ minWidth: "40px", p: 1 }}
            >
              <SearchIcon />
            </Button>
          </form>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{ backgroundColor: "#fff" }}
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>
        </Box>
      </Box>

      {/* Status Tabs */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          {STATUS_TABS.map((tab, index) => {
            const countKey = tab.value === "" ? "all" : tab.value;
            return (
              <Tab
                key={index}
                label={`${tab.label} (${counts[countKey] || 0})`}
              />
            );
          })}
        </Tabs>
      </Box>

      <Box>
        <Table
          columns={[
            { field: "client_name", headerName: "Client Name" },
            { field: "client_phone", headerName: "Client Phone" },
            { field: "lift_type", headerName: "Lift Type" },
            { field: "capacity_persons", headerName: "Capacity (Persons)" },
            { field: "capacity_kg", headerName: "Capacity (Kg)" },
            { field: "no_of_floors", headerName: "No of Floors" },
            { field: "door_type", headerName: "Door Type" },
            { field: "lift_configuration", headerName: "Lift Configuration" },
            { field: "statusCustom", headerName: "Status" },
            { field: "creator.name", headerName: "Sales Person" },
          ]}
          data={leads}
          loading={loading}
          links={links}
          setCurrentPage={setCurrentPage}
          actions={actions}
          meta={meta}
          perPage={perPage}
          setPerPage={setPerPage}
        ></Table>
      </Box>
      <EditLeadDialog
        openEditDialog={openEditDialog}
        setOpenEditDialog={setOpenEditDialog}
        fetchingLead={fetchingLead}
        selectedLead={selectedLead}
        setSelectedLead={setSelectedLead}
        updateLead={updateLead}
        errors={errors}
        updatingLead={updatingLead}
      />

      {/* Reject Lead Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.dialog.borderRadius,
            minWidth: theme.dialog.minWidth,
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.dialog.headerBgColor,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={"bold"} color="error">
              Reject Lead
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={() => setOpenRejectDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this lead. This action cannot
            be undone.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            disabled={rejectingLead}
            error={!!rejectErrors.rejection_reason}
            helperText={rejectErrors.rejection_reason?.[0]}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: theme.dialog.headerBgColor,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={() => setOpenRejectDialog(false)}
            disabled={rejectingLead}
            sx={{
              backgroundColor: theme.button.backgroundColor,
              color: "#000",
              borderRadius: theme.button.borderRadius,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectLead}
            disabled={rejectingLead || !rejectionReason.trim()}
            sx={{
              backgroundColor: "#ef5350",
              borderRadius: theme.button.borderRadius,
              "&:hover": { backgroundColor: "#d32f2f" },
            }}
            startIcon={<Cancel />}
          >
            {rejectingLead ? "Rejecting..." : "Reject Lead"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default LeadManagementPage;
